import React from "react";
import {
  Box,
  Card,
  Grid,
  Typography,
  TextField,
  Stack,
} from "@mui/material";
import { Bar } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const adminCardSx = {
  borderRadius: "18px",
  border: "1px solid rgba(47,99,173,0.08)",
  boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
  backgroundColor: "rgba(255,255,255,0.96)",
};

const BillingSummaryOverview = ({
  data,
  loading,
  viewMode,
  subtypeMap,
  startDate,
  endDate,
  setStartDate,
  setEndDate,
}) => {
  const resolvedViewMode = viewMode || "monthly";

  const filteredData = data.filter((item) => {
    if (!item.sent_at) return false;
    const sent = dayjs(item.sent_at);
    const start = startDate ? dayjs(startDate) : null;
    const end = endDate ? dayjs(endDate) : null;

    return (
      (!start || sent.isSameOrAfter(start, "day")) &&
      (!end || sent.isSameOrBefore(end, "day"))
    );
  });

  const formatCurrency = (value) =>
    Number(value || 0).toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const formatNumber = (value) =>
    Number(value || 0).toLocaleString("en-US");

  const aggregateBillingByDate = () => {
    const grouped = {};

    filteredData.forEach((item) => {
      if (!item.billable || !item.work_order_id || !item.sent_at) return;

      const dateKey =
        resolvedViewMode === "daily"
          ? dayjs(item.sent_at).format("YYYY-MM-DD")
          : dayjs(item.sent_at).format("YYYY-MM");

      grouped[dateKey] = (grouped[dateKey] || 0) + parseFloat(item.price || 0);
    });

    const sortedKeys = Object.keys(grouped).sort();

    return {
      labels: sortedKeys,
      totals: sortedKeys.map((key) => grouped[key]),
    };
  };

  const getBilledCountBySubtype = () => {
    const map = {};

    filteredData.forEach((item) => {
      if (!item.billable || !item.work_order_id) return;

      const subtypeId = item.workorder_subtype_id;
      const subtypeName = (subtypeMap && subtypeMap[subtypeId]) || "Unknown";

      if (!map[subtypeName]) map[subtypeName] = new Set();
      map[subtypeName].add(item.work_order_id);
    });

    const labels = Object.keys(map).sort();
    const values = labels.map((label) => map[label].size);

    return { labels, values };
  };

  const getTotalByItemGroup = () => {
    const map = {};

    filteredData.forEach((item) => {
      if (!item.billable || !item.item_group) return;
      const key = item.item_group || "Unknown";
      map[key] = (map[key] || 0) + parseFloat(item.price || 0);
    });

    const labels = Object.keys(map);
    const values = labels.map((key) => map[key]);

    return { labels, values };
  };

  const getTotalByAssociationName = () => {
    const map = {};

    filteredData.forEach((item) => {
      if (!item.billable || !item.association_name) return;
      const name = item.association_name;
      map[name] = (map[name] || 0) + parseFloat(item.price || 0);
    });

    const labels = Object.keys(map);
    const values = labels.map((name) => map[name]);

    return { labels, values };
  };

  const { labels, totals } = aggregateBillingByDate();
  const { labels: subtypeLabels, values: subtypeCounts } = getBilledCountBySubtype();
  const { labels: itemGroupLabels, values: itemGroupValues } = getTotalByItemGroup();
  const { labels: assocLabels, values: assocValues } = getTotalByAssociationName();

  const billingChartData = {
    labels,
    datasets: [
      {
        label: `Total Billing (${resolvedViewMode})`,
        data: totals,
        backgroundColor: "#2f63ad",
        borderRadius: 6,
        maxBarThickness: 48,
      },
    ],
  };

  const subtypeChartData = {
    labels: subtypeLabels,
    datasets: [
      {
        label: "Work Orders Billed",
        data: subtypeCounts,
        backgroundColor: "#f59e0b",
        borderRadius: 6,
      },
    ],
  };

  const assocChartData = {
    labels: assocLabels,
    datasets: [
      {
        label: "Total Billing",
        data: assocValues,
        backgroundColor: "#8b5cf6",
        borderRadius: 6,
      },
    ],
  };

  const itemGroupChartData = {
    labels: itemGroupLabels,
    datasets: [
      {
        label: "Total Billing",
        data: itemGroupValues,
        backgroundColor: "#22c55e",
        borderRadius: 6,
      },
    ],
  };

  const moneyVerticalOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      datalabels: {
        color: "#ffffff",
        anchor: "center",
        align: "center",
        formatter: (value) => formatCurrency(value),
        font: {
          weight: "bold",
          size: 11,
        },
        clamp: true,
      },
      tooltip: {
        callbacks: {
          label: (ctx) => formatCurrency(ctx.parsed.y),
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (v) => formatCurrency(v),
        },
      },
      x: {
        ticks: {
          maxRotation: 0,
          minRotation: 0,
        },
      },
    },
  };

  const moneyHorizontalOptions = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      datalabels: {
        color: "#ffffff",
        anchor: "center",
        align: "center",
        formatter: (value) => formatCurrency(value),
        font: {
          weight: "bold",
          size: 11,
        },
        clamp: true,
      },
      tooltip: {
        callbacks: {
          label: (ctx) => formatCurrency(ctx.parsed.x),
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        ticks: {
          callback: (v) => formatCurrency(v),
        },
      },
    },
  };

  const countHorizontalOptions = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      datalabels: {
        color: "#1f2937",
        anchor: "end",
        align: "right",
        offset: 4,
        formatter: (value) => formatNumber(value),
        font: {
          weight: "bold",
          size: 11,
        },
        clamp: true,
      },
      tooltip: {
        callbacks: {
          label: (ctx) => `${formatNumber(ctx.parsed.x)} work orders`,
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        ticks: {
          precision: 0,
          stepSize: 1,
          callback: (v) => formatNumber(v),
        },
      },
    },
  };

  return (
    <>
      <Card sx={{ ...adminCardSx, p: 2.5, mb: 3 }}>
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 700, mb: 1, color: "#223142" }}
        >
          Filter by Date
        </Typography>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <TextField
            label="Start Date"
            type="date"
            value={startDate || ""}
            onChange={(e) => setStartDate(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="End Date"
            type="date"
            value={endDate || ""}
            onChange={(e) => setEndDate(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
        </Stack>
      </Card>

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={6}>
          <Card sx={{ ...adminCardSx, p: 2.5 }}>
            <Typography sx={{ fontWeight: 700, mb: 2, color: "#223142" }}>
              Total Billing ({resolvedViewMode})
            </Typography>
            <Box sx={{ height: 360 }}>
              {loading ? (
                <Typography>Loading...</Typography>
              ) : (
                <Bar
                  data={billingChartData}
                  options={moneyVerticalOptions}
                  plugins={[ChartDataLabels]}
                />
              )}
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ ...adminCardSx, p: 2.5 }}>
            <Typography sx={{ fontWeight: 700, mb: 2, color: "#223142" }}>
              Work Orders by Subtype
            </Typography>
            <Box sx={{ height: 360 }}>
              {loading ? (
                <Typography>Loading...</Typography>
              ) : (
                <Bar
                  data={subtypeChartData}
                  options={countHorizontalOptions}
                  plugins={[ChartDataLabels]}
                />
              )}
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ ...adminCardSx, p: 2.5 }}>
            <Typography sx={{ fontWeight: 700, mb: 2, color: "#223142" }}>
              Billing by Association
            </Typography>
            <Box sx={{ height: 360 }}>
              {loading ? (
                <Typography>Loading...</Typography>
              ) : (
                <Bar
                  data={assocChartData}
                  options={moneyHorizontalOptions}
                  plugins={[ChartDataLabels]}
                />
              )}
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ ...adminCardSx, p: 2.5 }}>
            <Typography sx={{ fontWeight: 700, mb: 2, color: "#223142" }}>
              Billing by Item Group
            </Typography>
            <Box sx={{ height: 360 }}>
              {loading ? (
                <Typography>Loading...</Typography>
              ) : (
                <Bar
                  data={itemGroupChartData}
                  options={moneyHorizontalOptions}
                  plugins={[ChartDataLabels]}
                />
              )}
            </Box>
          </Card>
        </Grid>
      </Grid>
    </>
  );
};

export default BillingSummaryOverview;